import moment from 'moment';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import ClockImage from '../../../../assets/images/time-clock.svg';
import {StyledButton, StyledLightRedButton} from '@components/button/button.style';
import {StyledIonModal} from '@components/modal/modal.style';
import {ContractAttachmentFolderFile} from '@models/contractAttachmentFolderFile';
import {StyledAttachementsActionSheetContainer} from './attachementActionSheet.style';
import {ContractAttachmentFolder} from "@models/contractAttachmentFolder";
import {blobToBase64, getExtension} from "@services/file.service";
import {API_ENDPOINT} from "../../../../variables/variables";
import {IonAlert, IonIcon, IonLoading} from "@ionic/react";
import {documentTextOutline} from "ionicons/icons";
import {getContractFolderFileBase64} from "@services/contract.service";
import {Device} from "@capacitor/device";
import {Directory, Filesystem} from "@capacitor/filesystem";
import {FileOpener} from "@capacitor-community/file-opener";
import {AndroidSettings, IOSSettings, NativeSettings} from "capacitor-native-settings";

type AttachementsActionSheetProps = {
    isVisible: boolean;
    contractId: bigint;
    file?: ContractAttachmentFolderFile;
    folder?: ContractAttachmentFolder;
    onRemove: () => void;
    onChangeName: () => void;
    onDismiss: () => void;
}

const AttachementsActionSheet: React.FC<AttachementsActionSheetProps> = (props: AttachementsActionSheetProps) => {
    const {t} = useTranslation();

    const [showPermissionAlert, setShowPermissionAlert] = useState<boolean>(false);
    const [showLoader, setShowLoader] = useState<boolean>(false);

    const openFile = async () => {
        if (!props.file || !props.folder) {
            return;
        }

        setShowLoader(true);

        const fileData = await getContractFolderFileBase64(props.contractId, props.folder.id, props.file.id)
            .then(response => {
                return response.data.data;
            })
            .catch(error => {
               setShowLoader(false);

               console.error(error);
            });

        const info = await Device.getInfo();

        let deniedPermissions = false;
        if (info.operatingSystem === 'android' && parseFloat(info.osVersion) < 13) {
            let hasPermissions = await Filesystem.checkPermissions();
            deniedPermissions = hasPermissions.publicStorage === 'denied';
        }

        if (deniedPermissions) {
            setShowPermissionAlert(true);
            setShowLoader(false);
            return;
        }

        const sanitizedName = props.file.original_name.replace(/\//g, '_');

        try {
            const result = await Filesystem.writeFile({
                path: `ExactPeople/${sanitizedName}`,
                data: fileData,
                directory: Directory.Library,
                recursive: true
            })

            const extension = (sanitizedName.split('.').pop() ?? '').toLowerCase();
            let contentType = 'application/octet-stream';

            if (extension === 'pdf') {
                contentType = 'application/pdf';
            } else if (['jpg', 'jpeg'].includes(extension)) {
                contentType = 'image/jpeg';
            } else if (extension === 'png') {
                contentType = 'image/png';
            }

            await FileOpener.open({
                'filePath': result.uri,
                'contentType': contentType,
            });

            setShowLoader(false);
        } catch (e) {
            console.error(e);
        }
    }

    return (
        <StyledIonModal
            isOpen={props.isVisible}
            onDidDismiss={() => props.onDismiss()}
            initialBreakpoint={0.7}
            breakpoints={[0, 0.7]}
        >
            <IonLoading isOpen={showLoader} onDidDismiss={() => setShowLoader(false)}/>
            <IonAlert
                isOpen={showPermissionAlert}
                onDidDismiss={() => setShowPermissionAlert(false)}
                header={t('common.file_permissions.alert')}
                buttons={[
                    {
                        text: t('common.alertCancel'),
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => {
                            setShowPermissionAlert(false);
                        },
                    },
                    {
                        text: t('common.file_permissions.goToAppSettings'),
                        handler: async () => {
                            setShowPermissionAlert(false);
                            NativeSettings.open({
                                optionAndroid: AndroidSettings.ApplicationDetails,
                                optionIOS: IOSSettings.App
                            })
                        }
                    }
                ]}
            />
            <StyledAttachementsActionSheetContainer className="t-p-50" onClick={(e) => e.stopPropagation()}>
                {
                    props.file?.original_name && getExtension(props.file?.original_name) !== 'pdf' &&
                    <img className="img-preview" src={API_ENDPOINT + 'file/uploads/' + props.file?.path} alt="img-preview"/>
                }
                {
                    props.file?.original_name && getExtension(props.file?.original_name) === 'pdf' &&
                    <IonIcon src={documentTextOutline} style={{"height": "80px", "fontSize": "40px", "color": '#888A98'}}/>
                }
                <span className="name">{props.file?.original_name}</span>
                <span className="date">
                    <img src={ClockImage} alt="img-clock"/> {moment(props.file?.created_at).format('DD.MM.YYYY')}
                </span>
                <StyledButton onClick={openFile}>{t("contractPage.attachements.actionSheet.open")}</StyledButton>
                {
                    !props.folder?.expiration_date && !props.folder?.qualification_date && (
                        <>
                            <StyledButton
                                onClick={(e) => {
                                    props.onChangeName();
                                    e.stopPropagation();
                                }}
                            >
                                {t("contractPage.attachements.actionSheet.changeName")}
                            </StyledButton>
                            <StyledLightRedButton
                                onClick={(e) => {
                                    props.onRemove();
                                    e.stopPropagation();
                                }}
                            >
                                {t("contractPage.attachements.actionSheet.delete")}
                            </StyledLightRedButton>
                        </>
                    )
                }
            </StyledAttachementsActionSheetContainer>
        </StyledIonModal>
    );
};

export default AttachementsActionSheet;